/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { service } from "@ember/service";
import Component from "@glimmer/component";
import type { EmberChangeset } from "ember-changeset";
import type IntlService from "ember-intl/services/intl";
import type FollowUpReason from "parog-web/models/follow-up-reason";
import type { FollowUpReasonStringEnum } from "parog-web/models/patient-enrolled-journal-event";
import type { TreatmentProtocol } from "parog-web/models/protocol";

interface Signature {
  Args: {
    /** Encapsulate a TreatmentHistoryChangedJournalEvent */
    changeset: EmberChangeset;
    followUpReasonId: FollowUpReason["id"];
  };
}

export default class TreatmentHistoryEditionFormProtocolsViewComponent extends Component<Signature> {
  @service declare intl: IntlService;

  get specificProtocols(): TreatmentProtocol[] {
    switch (this.args.followUpReasonId as FollowUpReasonStringEnum) {
      case "onco-breast-cancer":
      case "onco-breast-cancer-tnm0":
        return [
          "abemaciclib-verzenios",
          "ribociclib-kisqali",
          "raac-gynecological-surgery"
        ];
      case "onco-lung-cancer":
        return [
          "atezolizumab",
          "pembrozimulab",
          "carboplatine-alimta-pembrozimulab"
        ];
      case "onco-pancreatic-cancer":
        return [
          "regorafenib-stivarga-initial",
          "regorafenib-stivarga-renewal",
          "olaparib-initial",
          "olaparib-following"
        ];
      case "onco-prostate-cancer":
        return ["prostatectomy-total", "adt-anti-androgen", "adt-chemo"];
      case "onco-ovarian-cancer":
      case "onco-cervix-cancer":
        return ["raac-gynecological-surgery"];
      default:
        return [];
    }
  }

  get defaultProtocols(): TreatmentProtocol[] {
    return [
      "default-chemotherapy",
      "default-radiotherapy",
      "default-immunotherapy",
      "default-other-follow-up-events",
      "default-targeted-therapy",
      "default-surgery",
      "default-hormonotherapy"
    ];
  }

  get translatedAndSortedProtocols() {
    const defaultProtocols = this.defaultProtocols;

    const translatedProtocols = defaultProtocols.map(protocol => ({
      original: protocol,
      translated: this.intl.t(`treatment-protocol.${protocol}`)
    }));

    translatedProtocols.sort((a, b) =>
      a.translated.localeCompare(b.translated)
    );

    return translatedProtocols;
  }
}
